export function berlinDay(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const value = type => parts.find(part => part.type === type).value;
  return `${value('year')}-${value('month')}-${value('day')}`;
}

function parseDay(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('Invalid date');
  const date = new Date(`${value}T12:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) throw new Error('Invalid date');
  return date;
}

export function addWorkingDays(value, count, closed = []) {
  const date = parseDay(value);
  if (!Number.isInteger(count) || count < 0 || count > 100) throw new Error('Invalid duration');
  if (!Array.isArray(closed) || closed.length > 366) throw new Error('Invalid calendar');
  closed.forEach(parseDay);
  const holidays = new Set(closed);
  const working = () => ![0, 6].includes(date.getUTCDay()) && !holidays.has(date.toISOString().slice(0, 10));
  let guard = 0;
  while (count > 0 || !working()) {
    if (++guard > 800) throw new Error('Calendar has no working dates');
    date.setUTCDate(date.getUTCDate() + 1);
    if (working() && count > 0) count--;
  }
  return date.toISOString().slice(0, 10);
}

export function estimateDelivery(profile, now = new Date()) {
  if (!profile || profile.enabled !== true) return null;
  try {
    const order = berlinDay(now);
    const limits = { start_days: [0, 10], production_min: [1, 40], production_max: [1, 40], transit_min: [1, 40], transit_max: [1, 40], buffer_days: [0, 20] };
    for (const [key, [min, max]] of Object.entries(limits)) {
      if (!Number.isInteger(profile[key]) || profile[key] < min || profile[key] > max) return null;
    }
    if (profile.production_min > profile.production_max || profile.transit_min > profile.transit_max) return null;
    const productionClosed = profile.production_closed || [];
    const transitClosed = profile.transit_closed || [];
    let buffer = 0;
    if (profile.buffer_until) {
      parseDay(profile.buffer_until);
      if (order <= profile.buffer_until) buffer = profile.buffer_days;
    }
    const start = addWorkingDays(order, profile.start_days, productionClosed);
    const shipMin = addWorkingDays(start, profile.production_min, productionClosed);
    const shipMax = addWorkingDays(start, profile.production_max, productionClosed);
    const deliveryMin = addWorkingDays(shipMin, profile.transit_min, transitClosed);
    const deliveryMax = addWorkingDays(shipMax, profile.transit_max + buffer, transitClosed);
    return { order, start, shipMin, shipMax, deliveryMin, deliveryMax, buffer };
  } catch {
    return null;
  }
}

export function formatDateRange(start, end, locale = 'de-DE', currentYear = '') {
  const first = parseDay(start);
  const last = parseDay(end);
  const year = start.slice(0, 4) !== end.slice(0, 4) || start.slice(0, 4) !== currentYear ? 'numeric' : undefined;
  const formatter = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', day: '2-digit', month: 'short', year });
  if (start !== end && start.slice(0, 7) === end.slice(0, 7)) {
    const day = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', day: '2-digit' }).format(first);
    return `${day}${locale.startsWith('de') ? '.' : ''}–${formatter.format(last)}`;
  }
  return start === end ? formatter.format(first) : `${formatter.format(first)} – ${formatter.format(last)}`;
}

if (typeof customElements !== 'undefined' && !customElements.get('timond-delivery-estimate')) {
  customElements.define('timond-delivery-estimate', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('[data-delivery-open]');
      this.fallback = this.querySelector('[data-delivery-fallback]');
      try { this.profile = JSON.parse(this.dataset.profile); } catch { this.profile = null; }
      this.trigger.addEventListener('click', () => {
        if (this.refresh() && !this.dialog.open) this.dialog.showModal();
      }, options);
      this.querySelector('[data-delivery-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => {
        if (this.trigger.isConnected && !this.trigger.hidden) this.trigger.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refresh(); }, options);
      this.timer = setInterval(() => { if (!document.hidden) this.refresh(); }, 60000);
      this.refresh();
    }

    refresh() {
      const dates = estimateDelivery(this.profile);
      const valid = !!dates && typeof this.dialog.showModal === 'function';
      this.trigger.hidden = !valid;
      this.fallback.hidden = valid;
      this.querySelector('[data-delivery-summary]').hidden = !valid;
      if (!valid) {
        if (this.dialog.open) this.dialog.close();
        return false;
      }
      const locale = document.documentElement.lang.startsWith('de') ? 'de-DE' : 'en-GB';
      const year = dates.order.slice(0, 4);
      const set = (selector, value) => this.querySelectorAll(selector).forEach(node => { if (node.textContent !== value) node.textContent = value; });
      set('[data-order-date]', formatDateRange(dates.order, dates.order, locale, year));
      set('[data-ship-date]', formatDateRange(dates.shipMin, dates.shipMax, locale, year));
      set('[data-delivery-date]', formatDateRange(dates.deliveryMin, dates.deliveryMax, locale, year));
      const note = this.querySelector('[data-delivery-buffer]');
      note.hidden = !dates.buffer;
      set('[data-buffer-days]', String(dates.buffer));
      return true;
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearInterval(this.timer);
      if (this.dialog?.open) this.dialog.close();
    }
  });
}
